<template>
  <div class="hr-icon flex aspect-square items-center justify-center">
    <component
      :is="iconComponent"
      v-if="iconComponent"
      :class="{
        'text-xs': size === ComponentSize.sm,
        'text-base': size === ComponentSize.default,
        'text-[18px]': size === ComponentSize.md,
        'text-xl': size === ComponentSize.lg,
        'text-2xl': size === ComponentSize.xl,
        'text-[28px]': size === ComponentSize.xl2,
        'text-3xl': size === ComponentSize.xl3,
        'text-[32px]': size === ComponentSize.xl4,
        'text-5xl': size === ComponentSize.xl5,
        'text-6xl': size === ComponentSize.xl6,
      }"
      :weight="weightComp"
    />
    <Suspense v-else-if="customIcon">
      <nuxt-icon
        class="[&_svg]:mb-0"
        :name="customIcon.toString()"
        :filled="filled"
        :class="{
          'text-xs': size === ComponentSize.sm,
          'text-base': size === ComponentSize.default,
          'text-[18px]': size === ComponentSize.md,
          'text-xl': size === ComponentSize.lg,
          'text-2xl': size === ComponentSize.xl,
          'text-[28px]': size === ComponentSize.xl2,
          'text-3xl': size === ComponentSize.xl3,
          'text-[32px]': size === ComponentSize.xl4,
          'text-5xl': size === ComponentSize.xl5,
          'text-6xl': size === ComponentSize.xl6,
        }"
      />
      <template #fallback>
        <div class="border-fgr-secondary h-full w-full rounded-full border-2" />
      </template>
    </Suspense>
    <div v-else>
      <div class="border-fgr-secondary h-full w-full rounded-full border-2" />
    </div>
  </div>
</template>

<script setup lang="ts">
  defineOptions({
    name: "Icon",
  });

  // Cache for dynamically imported icons
  const iconCache = new Map();

  const props = withDefaults(
    defineProps<{
      filled?: boolean;
      size?: IconSize;
      src?: IconSource | CustomIconSource | string | "undefined" | null;
      weight?: string;
    }>(),
    {
      filled: false,
      size: ComponentSize.default,
      src: "",
      weight: undefined,
    }
  );

  const isCustomIcon = computed(() => {
    if (!props.src) return false;
    return props.src.startsWith("custom/");
  });

  const customIcon = computed(() => {
    if (!isCustomIcon.value) return null;
    return props.src?.replace("custom/", "");
  });

  const iconName = computed(() => {
    if (!props.src || props.src === "undefined") return "Question";
    if (isCustomIcon.value) return null;

    let name = props.src;

    if (name.includes("/")) {
      name = name.split("/")[1];
    }
    if (name.includes(".svg")) {
      name = name.split(".svg")[0];
    }

    return name
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join("");
  });

  const weightComp = computed(() => {
    if (!props.src) return "fill";
    if (props.filled) return "fill";
    if (props.weight) return props.weight;

    const style = props.src.split("/")[0];
    switch (style) {
      case "bold":
        return "bold";
      case "duotone":
        return "duotone";
      case "fill":
        return "fill";
      case "light":
        return "light";
      case "regular":
        return "regular";
      default:
        return "bold";
    }
  });

  const iconComponent = computed(() => {
    if (!iconName.value || isCustomIcon.value) return null;

    const phName = `Ph${iconName.value}`;
    const iconWeight = weightComp.value;

    // Return from cache if available
    const cacheKey = `${phName}-${iconWeight}`;
    if (iconCache.has(cacheKey)) {
      return iconCache.get(cacheKey);
    }

    // Dynamically import the icon using the correct format
    const asyncComponent = defineAsyncComponent({
      loader: async () => {
        try {
          // Import the full phosphor package dynamically
          const phosphor = await import("@phosphor-icons/vue");

          // Get the icon by its name from the imported module
          const iconComponent = phosphor[phName];

          if (!iconComponent) {
            throw new Error(`Icon not found: ${phName}`);
          }

          return iconComponent;
        } catch (error) {
          console.error(`Failed to load icon: ${phName}`, error);

          // Fallback to question icon
          const phosphor = await import("@phosphor-icons/vue");
          return phosphor.PhQuestion;
        }
      },
      loadingComponent: {
        template: '<div class="border-fgr-secondary h-full w-full rounded-full border-2 animate-pulse"></div>',
      },
      timeout: 3000,
    });

    // Cache the component
    iconCache.set(cacheKey, asyncComponent);
    return asyncComponent;
  });
</script>
